import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import ProposalService from '../../services/ProposalService';
import { ApexOptions } from 'apexcharts';

const deduplicateByMonth = (data: any[]) =>
  data.reduce((acc: any[], curr: any) => {
    if (!acc.find((item) => item.month === curr.month)) {
      acc.push(curr);
    }
    return acc;
  }, []);

const mapToSeriesData = (data: any[], monthToIndex: Record<string, number>) =>
  deduplicateByMonth(data).map((item: any) => ({
    x: monthToIndex[item.month],
    y: Number(item.count),
  }));

const ProposalChart: React.FC = () => {
  const [approvedData, setApprovedData] = useState<{ x: number; y: number }[]>([]);
  const [rejectedData, setRejectedData] = useState<{ x: number; y: number }[]>([]);
  const [manualFollowUpData, setManualFollowUpData] = useState<{ x: number; y: number }[]>([]);
  const [sentData, setSentData] = useState<{ x: number; y: number }[]>([]);
  const [proposalCountData, setProposalCountData] = useState<{ x: number; y: number }[]>([]);
  const [uniqueMonths, setUniqueMonths] = useState<string[]>([]);

  const fetchProposals = async () => {
    try {
      const response: any = await ProposalService.monthlyCounts();

      if (response && response.data) {
        const approved = response.data.approved || [];
        const rejected = response.data.rejected || [];
        const manualFollowUp = response.data.manualFollowUp || [];
        const sent = response.data.sent || [];
        const proposalCount = response.data.proposalCount || [];

        // Collect unique months from all series
        const monthsSet = new Set<string>();
        approved.forEach((item: any) => monthsSet.add(item.month));
        rejected.forEach((item: any) => monthsSet.add(item.month));
        manualFollowUp.forEach((item: any) => monthsSet.add(item.month));
        sent.forEach((item: any) => monthsSet.add(item.month));
        proposalCount.forEach((item: any) => monthsSet.add(item.month));

        // Convert to an array and (optionally) sort them if needed
        const monthsArray = Array.from(monthsSet);

        // Map each month to a numeric index
        const monthToIndex: { [key: string]: number } = {};
        monthsArray.forEach((m, i) => {
          monthToIndex[m] = i;
        });

        // Create series data with x as numeric index
        const approvedSeriesData = mapToSeriesData(approved, monthToIndex);
        const rejectedSeriesData = mapToSeriesData(rejected, monthToIndex);
        const manualFollowUpSeriesData = mapToSeriesData(manualFollowUp, monthToIndex);
        const sentSeriesData = mapToSeriesData(sent, monthToIndex);
        const proposalCountSeriesData = mapToSeriesData(proposalCount, monthToIndex);

        setUniqueMonths(monthsArray);
        setApprovedData(approvedSeriesData);
        setRejectedData(rejectedSeriesData);
        setManualFollowUpData(manualFollowUpSeriesData);
        setSentData(sentSeriesData);
        setProposalCountData(proposalCountSeriesData);
      } else {
        console.error('API response is missing or invalid:', response);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  const options: ApexOptions = {
    chart: {
      type: 'line',
      height: 350,
      background: '#ffffff',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
    },
    // Seri renkleri (pastel): Sözleşme (yeşil), Olumsuz (kırmızı), Manuel Takip (lavanta), Takip Edilen (mavi)
    colors: ['#95de64', '#eb372aff', '#A8B5DF', '#91caff', '#FF7A00'],
    stroke: {
      curve: 'smooth',
      width: [3, 3, 3, 3, 4],
      dashArray: [0, 5, 0, 0, 0],
    },
    xaxis: {
      type: 'numeric',
      min: 0, 
      tickAmount: uniqueMonths.length > 1 ? uniqueMonths.length - 1 : 0,
      labels: {
        style: { colors: '#6c757d' },
        formatter: (val: any) => {
          // Convert numeric index back to month name
          return uniqueMonths[val] !== undefined ? uniqueMonths[val] : '';
        }
      },
      // Ensure the first data point is at x=0 (y-axis), no extra offset needed
    },
    yaxis: {
      labels: {
        formatter: (val: number) => val.toString(),
        style: { colors: '#6c757d' },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (val: number) => {
          // Show month name in tooltip
          return uniqueMonths[val] !== undefined ? uniqueMonths[val] : '';
        },
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      itemMargin: { horizontal: 10, vertical: 5 },
    },
  };

  const series = [
    { name: 'Sözleşme', data: approvedData },
    { name: 'Olumsuz', data: rejectedData },
    { name: 'Manuel Takip', data: manualFollowUpData },
    { name: 'Takip', data: sentData },
    { name: 'Teklif Sayısı', data: proposalCountData },
  ];

  return (
    <div>
      <h3 style={{ color: '#001F3F', fontSize: 24 }}>Teklifler</h3>
      {series.some(s => s.data.length > 0) ? (
        <Chart options={options} series={series} type="line" height={410} />
      ) : (
        <p>Veriler yükleniyor...</p>
      )}
    </div>
  );
};

export default ProposalChart;
